import React from "react";
import dayjs from "dayjs";
import {
	Avatar,
	Col,
	DatePicker,
	Form,
	Input,
	Row,
	Typography,
} from "antd";
import {
	HomeOutlined,
	MailOutlined,
	PhoneOutlined,
	UserOutlined,
} from "@ant-design/icons";
import SectionCard from "../../../../components/shared/SectionCard";
import { Customer } from "../../../../types/inventory/CustomerTypes";
import { CustomerProfileDraft } from "../../../../types/inventory/customerRecord";

const { Text } = Typography;

/** CU-86d44j2ac — customer Personal Info on generic user storage only; nothing here reads or writes patient_info. */
interface CustomerPersonalInfoTabProps {
	customer: Customer;
	draft: CustomerProfileDraft;
	onFieldChange: (field: keyof CustomerProfileDraft, value: string | null) => void;
}

const CustomerPersonalInfoTab: React.FC<CustomerPersonalInfoTabProps> = ({
	customer,
	draft,
	onFieldChange,
}) => {
	const currentProfilePicture = draft.profile_picture || "/assets/images/default-profile.png";

	const fi = { marginBottom: 8 };

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/* The picture route is self-only and ignores user_id, so staff get no upload or delete here (14ygtkjcvyx). */}
			<div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)", borderRadius: 8 }}>
				<Avatar size={52} src={currentProfilePicture} alt="Customer avatar" style={{ border: "2px solid var(--mg-border)", flexShrink: 0 }} />
			</div>

			{/* CU-86d45b54n: the customer primary name — no One Name Only control; a patient row's effective change still routes through the canonical Patient API. */}
			<SectionCard title="Primary Name">
				<Form layout="vertical" size="small">
					<Row gutter={[8, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Given Name" style={fi}>
								<Input
									aria-label="Given Name"
									value={draft.first_name}
									onChange={(e) => onFieldChange("first_name", e.target.value)}
									placeholder="Given name"
									prefix={<UserOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Middle Name" style={fi}>
								<Input
									aria-label="Middle Name"
									value={draft.middle_name}
									onChange={(e) => onFieldChange("middle_name", e.target.value)}
									placeholder="Middle name (optional)"
									prefix={<UserOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Family Name" style={fi}>
								<Input
									aria-label="Family Name"
									value={draft.last_name}
									onChange={(e) => onFieldChange("last_name", e.target.value)}
									placeholder="Family name"
									prefix={<UserOutlined />}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			<SectionCard title="Basic Information">
				<Form layout="vertical" size="small">
					<Row gutter={[8, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Username" style={fi}>
								<Input
									aria-label="Username"
									value={draft.username}
									onChange={(e) => onFieldChange("username", e.target.value)}
									prefix={<UserOutlined />}
									placeholder="Username"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Gender" style={fi}>
								<Input
									aria-label="Gender"
									value={draft.gender}
									onChange={(e) => onFieldChange("gender", e.target.value)}
									placeholder="Gender"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Sex" style={fi}>
								<Input
									aria-label="Sex"
									value={draft.sex}
									onChange={(e) => onFieldChange("sex", e.target.value)}
									placeholder="Male / Female"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Date of Birth" style={fi}>
								<DatePicker
									aria-label="Date of Birth"
									value={draft.date_of_birth ? dayjs(draft.date_of_birth) : null}
									format="DD/MM/YYYY"
									placeholder="DD/MM/YYYY"
									inputReadOnly
									disabledDate={(d) => d && d.isAfter(dayjs())}
									onChange={(date) => onFieldChange("date_of_birth", date ? date.format("YYYY-MM-DD") : null)}
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Height" style={fi}>
								<Input
									aria-label="Height"
									value={draft.height}
									onChange={(e) => onFieldChange("height", e.target.value)}
									placeholder="Height"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Weight" style={fi}>
								<Input
									aria-label="Weight"
									value={draft.weight}
									onChange={(e) => onFieldChange("weight", e.target.value)}
									placeholder="Weight"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			<SectionCard title="Contact & Address">
				<Form layout="vertical" size="small">
					<Row gutter={[8, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Email" style={fi}>
								<Input
									aria-label="Email"
									type="email"
									value={draft.email}
									onChange={(e) => onFieldChange("email", e.target.value)}
									placeholder="Email"
									prefix={<MailOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Phone" style={fi}>
								<Input
									aria-label="Phone"
									value={draft.phone}
									onChange={(e) => onFieldChange("phone", e.target.value)}
									placeholder="Phone number"
									prefix={<PhoneOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Address" style={fi}>
								<Input
									aria-label="Address"
									value={draft.address}
									onChange={(e) => onFieldChange("address", e.target.value)}
									placeholder="Street address"
									prefix={<HomeOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="City" style={fi}>
								<Input
									aria-label="City"
									value={draft.city}
									onChange={(e) => onFieldChange("city", e.target.value)}
									placeholder="City"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="State" style={fi}>
								<Input
									aria-label="State"
									value={draft.state}
									onChange={(e) => onFieldChange("state", e.target.value)}
									placeholder="State"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Country" style={fi}>
								<Input
									aria-label="Country"
									value={draft.country}
									onChange={(e) => onFieldChange("country", e.target.value)}
									placeholder="Country"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Post Code" style={fi}>
								<Input
									aria-label="Post Code"
									value={draft.postal_code}
									onChange={(e) => onFieldChange("postal_code", e.target.value)}
									placeholder="Post code"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			{/* CU-86d44j2ac: no ethnicities or additional emails — those are patient_info storage. */}
			<SectionCard title="Additional Details">
				<Row gutter={[8, 8]}>
					<Col xs={24} md={8}>
						<Text type="secondary" style={{ fontSize: 12, display: "block" }}>Account Type</Text>
						<Text>{customer.user_type || "—"}</Text>
					</Col>
					<Col xs={24} md={8}>
						<Text type="secondary" style={{ fontSize: 12, display: "block" }}>Shopify Customer ID</Text>
						<Text>{customer.shopify_customer_id ? String(customer.shopify_customer_id) : "—"}</Text>
					</Col>
					<Col xs={24} md={8}>
						<Text type="secondary" style={{ fontSize: 12, display: "block" }}>Account Status</Text>
						<Text>{customer.is_active === false ? "Deactivated" : "Active"}</Text>
					</Col>
				</Row>
			</SectionCard>
		</div>
	);
};

export default CustomerPersonalInfoTab;
